import { cn } from "cn";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import type * as React from "react";

// v3.2 notices: a tinted block that says what happened and what to do
// (Notice), and a one-paragraph note inside a form (InlineNotice). Tones
// use the status colours: error = record red, warn = amber, ok = green,
// info = brand blue.

export type NoticeTone = "error" | "warn" | "info" | "ok";

const TINT: Record<NoticeTone, string> = {
  error: "bg-v3-rec-tint",
  warn: "bg-v3-warn-tint",
  info: "bg-v3-brand-tint",
  ok: "bg-v3-ok-tint",
};
const INK: Record<NoticeTone, string> = {
  error: "text-v3-rec-text",
  warn: "text-v3-warn-text",
  info: "text-v3-brand-strong",
  ok: "text-v3-ok-text",
};
const ICON: Record<NoticeTone, React.ReactNode> = {
  error: <CircleAlert />,
  warn: <TriangleAlert />,
  info: <Info />,
  ok: <CircleCheck />,
};

/**
 * A notice block (a visit that failed, a payment problem): icon, title,
 * description and actions (buttons, stacked on phones, on the right from
 * 640px). Errors are announced (role=alert), the rest politely.
 */
export function Notice({
  tone = "info",
  title,
  description,
  icon,
  actions,
  className,
  testId,
}: {
  tone?: NoticeTone;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Replaces the tone's icon. */
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-[14px] p-4 font-v3 text-v3-text sm:flex-row sm:items-center",
        TINT[tone],
        className,
      )}
      data-slot="notice"
      data-testid={testId}
      data-tone={tone}
      role={tone === "error" ? "alert" : "status"}
    >
      <div className="flex min-w-0 flex-1 gap-3">
        <span
          aria-hidden
          className={cn("mt-0.5 flex shrink-0 [&_svg]:size-5", INK[tone])}
        >
          {icon ?? ICON[tone]}
        </span>
        <div className="min-w-0">
          <p className={cn("text-[14px] font-semibold", INK[tone])}>{title}</p>
          {description && (
            <p className="mt-0.5 text-[13.5px] leading-[1.45] text-v3-text-2">
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * A short note inside a form or sheet. With an icon: the text in ink beside
 * a tinted icon (the privacy note). Without: the text itself in the tone's
 * colour (an error message under a form).
 */
export function InlineNotice({
  tone = "info",
  icon,
  children,
  className,
  ...props
}: React.ComponentPropsWithRef<"p"> & {
  tone?: NoticeTone;
  /** A lucide icon element; omit for a plain message. */
  icon?: React.ReactNode;
}): React.JSX.Element {
  return (
    <p
      className={cn(
        "rounded-[12px] px-3.5 py-3 text-[13.5px]",
        TINT[tone],
        icon
          ? "flex gap-2.5 leading-snug text-v3-text"
          : cn("font-medium", INK[tone]),
        className,
      )}
      data-slot="inline-notice"
      data-tone={tone}
      {...props}
    >
      {icon && (
        <span
          aria-hidden
          className={cn("mt-px flex shrink-0 [&_svg]:size-[18px]", INK[tone])}
        >
          {icon}
        </span>
      )}
      {children}
    </p>
  );
}
